import { FreightCategoryEntity, FreightHandlingEntity } from 'Models/Entities';
import { FormattedMessage, useIntl } from 'react-intl';
import { FreightTypeDto } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { stringNotEmpty } from 'Util/TypeGuards';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { NumberInput } from 'Views/Components/New/NumberInput';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { TextInput } from 'Views/Components/New/TextInput';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { FreightBookingWizardData } from '../../BookingWizardData/FreightBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { WizardErrors } from '../../BookingWizardTypes';
import { ShipmentDimensionsSection } from './ShipmentDimensionsSection';

export interface ShipmentDescriptionSectionProps {
	typedErrors: WizardErrors<FreightBookingWizardData>;
	freightTypeOptions: { [key in string]: FreightTypeDto[] };
	freightCategoryOptions: FreightCategoryEntity[];
	freightHandlingOptions: FreightHandlingEntity[];
	onAfterChange: (fieldName: keyof FreightBookingWizardData) => void;
	showDimensions?: boolean;
}

export function ShipmentDescriptionSection({
	typedErrors,
	freightTypeOptions,
	freightCategoryOptions,
	freightHandlingOptions,
	onAfterChange,
	showDimensions,
}: ShipmentDescriptionSectionProps) {
	const intl = useIntl();

	const { wizardData, setWizardData, setInnerWizardErrors } = useBookingWizardDataStore();

	const inner = wizardData.innerWizardData as FreightBookingWizardData;

	const allTheSame = (innerWizardData: FreightBookingWizardData, oldFreightType?: FreightTypeDto) => {
		const oldLength = oldFreightType?.standardLength;
		const oldWidth = oldFreightType?.standardWidth;
		const oldHeight = oldFreightType?.standardHeight;
		const oldWeight = oldFreightType?.minimumWeight;
		const oldDescription = stringNotEmpty(oldFreightType?.standardDescription)
			? oldFreightType?.standardDescription
			: oldFreightType?.label;

		return (
			(innerWizardData.length === oldLength || !innerWizardData.length) &&
			(innerWizardData.width === oldWidth || !innerWizardData.width) &&
			(innerWizardData.height === oldHeight || !innerWizardData.height) &&
			(innerWizardData.weight === oldWeight || !innerWizardData.weight) &&
			(innerWizardData.description === oldDescription || !innerWizardData.description)
		);
	};
	const onChangeFreightType = (freightTypeId: string, oldValue: string) => {
		setInnerWizardErrors({});

		const freightType = Object.values(freightTypeOptions ?? {})
			.flat()
			.find(x => x.id === freightTypeId);
		if (!freightType) {
			return;
		}

		const newData = new BookingWizardData({ ...wizardData });
		if (!(newData.innerWizardData instanceof FreightBookingWizardData)) {
			return;
		}

		const oldFreightType = Object.values(freightTypeOptions ?? {})
			.flat()
			.find(x => x.id === oldValue);

		if (allTheSame(newData.innerWizardData, oldFreightType)) {
			newData.innerWizardData.length = freightType.standardLength ?? null;
			newData.innerWizardData.width = freightType.standardWidth ?? null;
			newData.innerWizardData.height = freightType.standardHeight ?? null;
			newData.innerWizardData.weight = freightType.minimumWeight ?? null;
		}

		newData.innerWizardData.description = freightType.standardDescription ?? freightType.label;

		setWizardData(newData);
	};

	const onChangeFreightCategory = (oldValue: string) => {
		const newData = new BookingWizardData({ ...wizardData });
		if (!(newData.innerWizardData instanceof FreightBookingWizardData)) {
			return;
		}

		const oldFreightType = Object.values(freightTypeOptions ?? {})
			.flat()
			.find(x => x.id === oldValue);

		if (allTheSame(newData.innerWizardData, oldFreightType)) {
			newData.innerWizardData.length = null;
			newData.innerWizardData.width = null;
			newData.innerWizardData.height = null;
			newData.innerWizardData.weight = null;
			newData.innerWizardData.description = '';
		}

		setWizardData(newData);
	};

	const freightCategorySelectOptions = freightCategoryOptions
		.filter(x => Object.keys(freightTypeOptions).includes(x.id) && freightTypeOptions[x.id].length > 0)
		.map(cat => ({ value: cat.id, label: cat.label }));

	const freightTypeSelectOptions =
		(inner?.type === 'freight' ? freightTypeOptions[inner.freightCategoryId] : [])?.map(type => ({
			value: type.id,
			label: type.label,
		})) ?? [];

	const handlingSelectOptions = freightHandlingOptions?.map(type => ({ value: type.id, label: type.label })) ?? [];

	return (
		<section className="shipment-section booking-section-card">
			<header className="booking-section-header">
				<span className="booking-section-heading__icon">
					<Icon name="description" useMaterialIcons />
				</span>
				<div className="booking-section-header__text">
					<h4 className="booking-section-title">
						<FormattedMessage
							id="freight-booking-wizard__shipment-tab__freight-description-section__label"
							defaultMessage="Shipment description"
						/>
					</h4>
				</div>
			</header>
			<div className="shipment-section-body grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
				<div data-test-id="freight-category-field">
					<SelectInput
						label={intl.formatMessage({
							id: 'freight-booking-wizard__shipment-tab__freight-category__label',
							defaultMessage: 'Freight category',
						})}
						placeholder={intl.formatMessage({
							id: 'freight-booking-wizard__shipment-tab__freight-category__placeholder',
							defaultMessage: 'Select freight category',
						})}
						isSearchable
						required
						options={freightCategorySelectOptions}
						value={freightCategorySelectOptions.find(o => o.value === inner?.freightCategoryId) ?? null}
						onChange={(option: any) => {
							const oldFreightType = inner.freightTypeId;
							inner.freightCategoryId = option?.value ?? '';
							onAfterChange('freightCategoryId');
							onChangeFreightCategory(oldFreightType);
						}}
						error={typedErrors?.freightCategoryId}
					/>
				</div>
				<div data-test-id="freight-type-field">
					<SelectInput
						label={intl.formatMessage({
							id: 'freight-booking-wizard__shipment-tab__freight-type__label',
							defaultMessage: 'Freight type',
						})}
						placeholder={intl.formatMessage({
							id: 'freight-booking-wizard__shipment-tab__freight-type__placeholder',
							defaultMessage: 'Select freight type',
						})}
						isSearchable
						required
						isDisabled={inner?.type === 'freight' && !inner?.freightCategoryId}
						options={freightTypeSelectOptions}
						value={freightTypeSelectOptions.find(o => o.value === inner?.freightTypeId) ?? null}
						onChange={(option: any) => {
							const oldValue = inner.freightTypeId;
							inner.freightTypeId = option?.value ?? '';
							onChangeFreightType(inner.freightTypeId, oldValue ?? '');
							onAfterChange('freightTypeId');
						}}
						error={typedErrors?.freightTypeId}
					/>
				</div>
				<div data-test-id="freight-quantity-field">
					<NumberInput
						label={intl.formatMessage({
							id: 'freight-booking-wizard__shipment-tab__freight-quantity__label',
							defaultMessage: 'Quantity',
						})}
						placeholder={intl.formatMessage({
							id: 'freight-booking-wizard__shipment-tab__freight-quantity__placeholder',
							defaultMessage: 'Enter quantity',
						})}
						required
						allowDecimals={false}
						allowNegative={false}
						value={inner?.quantity ?? undefined}
						onChange={value => {
							inner.quantity = value ?? null;
							onAfterChange('quantity');
						}}
						error={typedErrors?.quantity}
					/>
				</div>
				{freightHandlingOptions.length > 0 && (
					<SelectInput
						label={intl.formatMessage({
							id: 'freight-booking-wizard__shipment-tab__freight-handling__label',
							defaultMessage: 'Handling',
						})}
						placeholder={intl.formatMessage({
							id: 'freight-booking-wizard__shipment-tab__freight-handling__placeholder',
							defaultMessage: 'Select handling',
						})}
						isMulti
						isSearchable={false}
						options={handlingSelectOptions}
						value={handlingSelectOptions.filter(o => (inner?.handlingIds ?? []).includes(o.value))}
						onChange={(selected: any) => {
							inner.handlingIds = (selected ?? []).map((o: any) => o.value);
							onAfterChange('handlingIds');
						}}
						error={typedErrors?.handlingIds}
					/>
				)}
				<div data-test-id="freight-description-field">
					<TextInput
						label={intl.formatMessage({
							id: 'freight-booking-wizard__shipment-tab__freight-description__label',
							defaultMessage: 'Description',
						})}
						placeholder={intl.formatMessage({
							id: 'freight-booking-wizard__shipment-tab__freight-description__placeholder',
							defaultMessage: 'Enter description',
						})}
						required
						value={inner?.description ?? ''}
						onChange={e => {
							inner.description = e.target.value;
							onAfterChange('description');
						}}
						error={typedErrors?.description}
					/>
				</div>
			</div>
			{showDimensions && <ShipmentDimensionsSection typedErrors={typedErrors} onAfterChange={onAfterChange} />}
		</section>
	);
}
